import { JSX } from "react";

import { Alert, LinearProgress, Grid, Card, CardContent, Typography, useTheme } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { fetchAvailableDashboards } from "src/api/extendedAnalytics.api";
import RefreshButton from "src/components/TopNav/components/RefreshButton";
import TopNav from "src/components/TopNav/TopNav";
import sharedStyles from "src/constants/styles";
import { formatDate, formatDateStringToDayjsDate } from "src/lib/date.helper";
import { type SupersetAssetsDTO } from "src/types/superset";

const styles = {
  fullWidth: { xs: 12 },
  dashboard: { xs: 12, md: 4, lg: 3 },
};

function ExtendedAnalyticsDashboardSelectionPage(): JSX.Element {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const { data, isLoading, error } = useQuery<SupersetAssetsDTO>({
    queryKey: ["availableDashbaords"],
    queryFn: async () => fetchAvailableDashboards(),
    enabled: true,
    retry: 1,
    refetchInterval: false,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  return (
    <>
      <TopNav refreshButton={RefreshButton()}>
        <Typography variant='h3' sx={sharedStyles(theme).topNavTitleText}>
          {t("Insight Dashboards")}
        </Typography>
      </TopNav>
      <Grid container spacing={2} px={1}>
        <Grid size={styles.fullWidth}>
          {isLoading && <LinearProgress />}
          {!!error && <Alert severity='error'>{t("There was an error while fetching extended analytics dashboards")}</Alert>}
          {data && data.result.length === 0 && (
            <Alert severity='warning'>{t("There are no extended analytics dashboards available for your user")}</Alert>
          )}
          {data?.result && (
            <Grid container spacing={2}>
              {data.result.map((dashboard) => (
                <Grid size={styles.dashboard} key={dashboard.id}>
                  <Card
                    onClick={() => {
                      navigate(`/extended-analytics/${dashboard.id}`);
                    }}
                  >
                    <CardContent>
                      <Typography>{dashboard.dashboard_title}</Typography>
                      <Typography fontSize={theme.fontSizes.sm} color={theme.palette.text.secondary}>
                        {t("Edited on:")} {formatDate(formatDateStringToDayjsDate(dashboard.changed_on_utc))}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Grid>
      </Grid>
    </>
  );
}
export default ExtendedAnalyticsDashboardSelectionPage;
